import { colorConstants } from 'config/colorConstants';
import { maxPercentageValue, minPercentageValue } from './attributeSalesDataParser';
import { getAbsoluteChange, getPercentageChange, getPercentageChangeV2 } from 'utils/helperMethods';
import { salesTileKeyType } from 'components/pages/GrowthAnalysis';
import dayjs from 'dayjs';
import _ from 'lodash';

export function attributePriceChangeSalesDataParser(data: any) {
    const abs_volume: any = { data: [], minVal: 0, maxVal: 0 },
        percentage_volume: any = { data: [], minVal: minPercentageValue, maxVal: maxPercentageValue };
    const result = [abs_volume, percentage_volume];
    try {
        const graphDataKey = 'graphData';
        let bestMaxPercentageValue = 0;
        let bestMinPercentageValue = 0;
        data.forEach((item: any, idx: number) => {
            const salesVolumeAbs = getAbsoluteChange(
                item[graphDataKey][1]['salesData'],
                item[graphDataKey][0]['salesData'],
            );

            const salesVolumePercentage = getPercentageChangeV2(
                item[graphDataKey][1]['avgPrice'],
                item[graphDataKey][0]['avgPrice'],
            );

            bestMaxPercentageValue = Math.max(bestMaxPercentageValue, salesVolumePercentage);
            bestMinPercentageValue = Math.min(bestMinPercentageValue, salesVolumePercentage);
            const absVolumeTempObj = {
                sales: salesVolumeAbs,
                labelSales: salesVolumeAbs,
                category: item.attributeName,
                color: Number(salesVolumeAbs) < 0 ? colorConstants.red : colorConstants.limeGreen,
            };

            const percentageChangeVolumeTempObj = {
                sales:
                    salesVolumePercentage >= 0
                        ? Math.min(salesVolumePercentage, maxPercentageValue)
                        : Math.max(salesVolumePercentage, minPercentageValue),
                labelSales: salesVolumePercentage,
                category: item.attributeName,
                color: Number(salesVolumePercentage) < 0 ? colorConstants.red : colorConstants.limeGreen,
            };

            // show dark blue for industru avg
            if (item.attributeName === 'Average of Selection') {
                percentageChangeVolumeTempObj.color = colorConstants.purpleFont;
            }

            abs_volume.data.push(absVolumeTempObj);
            abs_volume.minVal = Math.min(abs_volume.minVal, salesVolumeAbs);
            abs_volume.maxVal = Math.max(abs_volume.maxVal, salesVolumeAbs);

            percentage_volume.data.push(percentageChangeVolumeTempObj);
        });

        percentage_volume.maxVal =
            bestMaxPercentageValue >= 0 && bestMaxPercentageValue < 200 ? bestMaxPercentageValue : maxPercentageValue;
        percentage_volume.minVal =
            bestMinPercentageValue <= 0 && bestMinPercentageValue > -100 ? bestMinPercentageValue : minPercentageValue;

        return result;
    } catch (error) {
        return result;
    }
}

export function attributePriceChangeSalesTableParser(
    data: any,
    from: salesTileKeyType = 'sales',
    volTile: any,
    showCurrency = '',
    selectedKpi: any,
    currencyUnitText: string,
    priceChangeConfig: any,
) {
    let result: any[] = [];
    const changeMixPercentLabel = 'Change due to mix(%)';
    const actualChangePercentLabel = 'Actual change in price(%)';
    const { hiddenTableRows = [] } = priceChangeConfig;
    const hideMixPercent = hiddenTableRows?.includes(changeMixPercentLabel);
    const hideActualChangePercent = hiddenTableRows?.includes(actualChangePercentLabel);

    //only show dollar symbol for value and profit
    const priceChangeTableMapping: any = {
        avgPriceCY: `${selectedKpi?.label}`,
        avgPriceYA: `${selectedKpi?.label} YA`,
        absValueChangePrice: `${selectedKpi?.label} Change`,
        absValueChangePercent: `${selectedKpi?.label} Change %`,
        cySales: `${currencyUnitText} Sales`,
        yaSales: `${currencyUnitText} Sales YA`,
        yoySalesChangePercent: `YoY Sales ${showCurrency} change %`,
        actualChangePercent: actualChangePercentLabel,
        changeMixPercent: changeMixPercentLabel,
    };
    try {
        //for sales tab
        const graphDataKey = 'graphData';
        let avgPriceCY: any = { type: priceChangeTableMapping.avgPriceCY },
            avgPriceYA: any = { type: priceChangeTableMapping.avgPriceYA },
            cySales: any = { type: priceChangeTableMapping.cySales },
            yaSales: any = { type: priceChangeTableMapping.yaSales },
            absValueChangePrice: any = { type: priceChangeTableMapping.absValueChangePrice },
            absValueChangePercent: any = { type: priceChangeTableMapping.absValueChangePercent },
            yoySalesChangePercent: any = { type: priceChangeTableMapping.yoySalesChangePercent },
            actualChangePercent: any = { type: priceChangeTableMapping.actualChangePercent },
            changeMixPercent: any = { type: priceChangeTableMapping.changeMixPercent };

        data.forEach((item: any) => {
            const attributeName = item.attributeName + ' ';
            // avg price
            avgPriceCY[attributeName] = [{ data: _.round(item[graphDataKey][0]['avgPrice'], 2), unit: showCurrency }];
            avgPriceYA[attributeName] = [{ data: _.round(item[graphDataKey][1]['avgPrice'], 2), unit: showCurrency }];

            //  absolute value change
            absValueChangePrice[attributeName] = [
                {
                    data: _.round(
                        getAbsoluteChange(item[graphDataKey][1]['avgPrice'], item[graphDataKey][0]['avgPrice']),
                        2,
                    ),
                    unit: showCurrency,
                },
            ];
            absValueChangePercent[attributeName] = [
                {
                    data: getPercentageChangeV2(item[graphDataKey][1]['avgPrice'], item[graphDataKey][0]['avgPrice']),
                    unit: '%',
                },
            ];

            //SALES
            cySales[attributeName] = [{ data: item[graphDataKey][0]['salesData'], unit: showCurrency }];
            yaSales[attributeName] = [{ data: item[graphDataKey][1]['salesData'], unit: showCurrency }];

            // yoySalesChangePercent
            yoySalesChangePercent[attributeName] = [
                {
                    data:
                        getPercentageChange(item[graphDataKey][1]['salesData'], item[graphDataKey][0]['salesData']) *
                        -1,
                    unit: '%',
                },
            ];

            // actualChangePercent
            actualChangePercent[attributeName] = [
                {
                    data: getPercentageChangeV2(
                        item[graphDataKey][1]['actualAvgPrice'],
                        item[graphDataKey][0]['actualAvgPrice'],
                    ),
                    unit: '%',
                },
            ];

            // changeMix
            changeMixPercent[attributeName] = [
                {
                    data: _.round(
                        absValueChangePercent[attributeName][0].data - actualChangePercent[attributeName][0].data,
                        2,
                    ),
                    unit: '%',
                },
            ];
        });

        result.push(
            avgPriceCY,
            avgPriceYA,
            absValueChangePrice,
            absValueChangePercent,
            cySales,
            yaSales,
            yoySalesChangePercent,
            ...(!hideActualChangePercent ? [actualChangePercent] : []),
            ...(!hideMixPercent ? [changeMixPercent] : []),
        );

        return result;
    } catch (error) {
        console.log(error, 'logging e');
        return result;
    }
}

export const priceChangeSalesTrendChartParser = (data: any[], appConfig: any) => {
    try {
        const lineChartResult: any = { minVal: Number.MAX_SAFE_INTEGER, maxVal: Number.MIN_SAFE_INTEGER, data: [] };
        const barChartResult: any = { minVal: 0, maxVal: 0, data: [] };
        data.forEach((item: any, index: number) => {
            const percentageChange = item.percentagePriceChange;
            lineChartResult.minVal = Math.min(
                lineChartResult?.minVal,
                item?.priceCurrent || Number.MAX_SAFE_INTEGER,
                item?.pricePrevious || Number.MAX_SAFE_INTEGER,
            );
            lineChartResult.maxVal = Math.max(
                lineChartResult?.maxVal,
                item?.priceCurrent || Number.MIN_SAFE_INTEGER,
                item?.pricePrevious || Number.MIN_SAFE_INTEGER,
            );
            barChartResult.minVal = Math.min(barChartResult?.minVal, percentageChange);
            barChartResult.maxVal = Math.max(barChartResult?.maxVal, percentageChange);
            const lineChartTempObj = {
                period: dayjs(item?.startTimestamp).format(appConfig?.commonConfig?.dateFormatAlt),
                'Current Year': item?.priceCurrent,
                'Year Ago': item?.pricePrevious,
            };

            const barChartTempObj = {
                category: dayjs(item?.startTimestamp).format(appConfig?.commonConfig?.dateFormatAlt),
                sales: percentageChange,
                color: percentageChange >= 0 ? colorConstants.limeGreen : colorConstants.red,
            };
            lineChartResult?.data?.push(lineChartTempObj);
            barChartResult?.data?.push(barChartTempObj);
        });
        return { lineChartResult, barChartResult };
    } catch (error) {
        throw error;
    }
};
